import { supabaseSyncProvider } from '@platform/cloud/supabaseProvider';
import { registerSupabaseDevice } from '@data/sync/supabaseSync';
import type { SyncChange } from '@domain/sync/changeModel';
import { ProviderError, type SyncProvider } from '@platform/cloud/types';
import type { DNRDatabase } from '@data/db/database';
import { SyncEngine } from './SyncEngine';
import { applyChanges } from './applyChanges';
import { currentDeviceId, ensureDevice } from './deviceIdentity';
import { setActiveBusinessId, healActiveBusinessPointer } from '@data/services/businessSession';
import { setSyncProvider } from './syncConfig';
import { recoverBusinessEntity, type RecoverBusinessError } from './recoverBusinessEntity';
import type { OutboxStatus } from '@data/db/types';

/**
 * Device registration is deliberately a parameter, not a hard import of the
 * Supabase singleton. `registerDevice` lives on `SupabaseSyncProvider` rather
 * than on the `SyncProvider` interface — it is a row-store ownership concern
 * that the generic transport does not model — so a test that injects the fake
 * provider has no device RPC to intercept otherwise. Production passes nothing
 * and registers against Supabase exactly as before.
 */
type DeviceRegistrar = (input: {
  readonly businessId: string;
  readonly deviceId: string;
  readonly label: string;
  readonly platform: string;
}) => Promise<void>;

/**
 * A synced business owned by the signed-in account that is not on this device.
 *
 * The display name is best-effort: the change log always carries the business
 * row (businessService enqueues it on create), so a recently synced business
 * names itself. A log that has compacted past the business row falls back to
 * the raw id rather than inventing a name.
 */
export interface CloudBusinessSummary {
  readonly businessId: string;
  readonly displayName: string | null;
}

export interface CloudRestoreResult {
  readonly businessId: string;
  readonly applied: number;
  readonly skipped: number;
  readonly cursor: string;
  readonly recovered: boolean;
  readonly pulled: number;
  /**
   * Whether a missing business entity was re-enqueued on the cloud.
   *
   * This is NOT a success signal: it is true when the local outbox entry was
   * reset, which happens BEFORE anything is uploaded. Always read `repair` to
   * find out whether the cloud actually ended up holding the row.
   */
  readonly businessRepublished?: boolean;
  /** Present only on the already-local branch. See `CloudRepairOutcome`. */
  readonly repair?: CloudRepairOutcome;
}

/**
 * What repairing an already-local business actually did.
 *
 * WHY THIS IS A RECORD AND NOT A BOOLEAN
 * --------------------------------------
 * A repair can fail in three unrelated ways, and they need three different
 * messages:
 *
 *   error             recovery refused to touch the outbox; nothing was sent
 *   pushError         the outbox was reset, but the sync cycle failed
 *   cloudHasBusiness  the upload was accepted — is the row actually there?
 *
 * Collapsing them was the defect this closes: `businessRepublished` answers
 * "was the entry reset", which is one step before an upload that may be
 * rejected. `SyncEngine.run()` never throws either — it records the failure in
 * the returned status — so a caller that reads only `recovered`/`pulled`
 * cannot tell a rejected push from a successful one.
 *
 * `cloudHasBusiness` is `null` when it cannot be determined. It is never
 * coerced to `false`, and never assumed `true`: an answer we do not have is a
 * third state, and a repair that guesses is how this class of bug survives.
 */
export interface CloudRepairOutcome {
  /** True when the acked outbox entry was reset to pending for a re-push. */
  readonly changed: boolean;
  /** True when the server already held the business row, so nothing was reset. */
  readonly serverHadBusiness: boolean;
  /** Recovery refused. Absent when recovery proceeded to the sync attempt. */
  readonly error?: { readonly code: RecoverBusinessError['code']; readonly message: string };
  /** Post-push check of the cloud snapshot. `null` = could not be determined. */
  readonly cloudHasBusiness: boolean | null;
  /** The provider permanently rejected the recovered business change. */
  readonly pushError?: string;
  /**
   * The business outbox entry's status AFTER the cycle.
   *
   * This is what separates "not yet" from "not there". `pending` means the
   * upload was deferred — a retryable transport or server condition, or a cycle
   * that never reached the push — so the cloud has no row because nothing was
   * sent, and the next sync owns it. `acked` with no row is the genuinely
   * alarming state: the server accepted the change and still cannot show it.
   * Calling both "permanently missing" told a user to reset changes that had
   * never failed.
   */
  readonly entryStatus?: OutboxStatus;
  /** Changes the provider accepted during the cycle that follows recovery. */
  readonly pushed: number;
  /** Changes permanently rejected during that cycle. */
  readonly failed: number;
  /** Rows applied from the pull. Unrelated to the business row. */
  readonly applied: number;
  /** Conflicts raised by the pull. Unrelated to the business row. */
  readonly conflicts: number;
}

function businessNameFrom(changes: readonly SyncChange[], businessId: string): string | null {
  const rows = changes.filter((c) => c.entityType === 'business' && c.entityId === businessId);
  if (rows.length === 0) return null;
  const latest = rows.reduce((a, b) => (b.version >= a.version ? b : a));
  const payload = latest.payload as { legalName?: unknown; tradeName?: unknown } | undefined;
  const legal =
    typeof payload?.legalName === 'string' && payload.legalName.trim()
      ? payload.legalName.trim()
      : null;
  const trade =
    typeof payload?.tradeName === 'string' && payload.tradeName.trim()
      ? payload.tradeName.trim()
      : null;
  return legal ?? trade;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * When a new device restore hits a missing business row, check whether the
 * provider's diagnostic function can confirm the business truly lacks a
 * business entity. If so, append an actionable hint to the error message.
 *
 * Returns an empty string if the provider cannot answer (so the generic
 * error message stands on its own).
 */
async function checkAndAdviseMissingBusiness(
  provider: SyncProvider,
  businessId: string,
  db: DNRDatabase,
): Promise<string> {
  if (!provider.checkBusinessRowExists) return '';
  try {
    const result = await provider.checkBusinessRowExists({ businessId });
    if (result === null) return '';
    const localHasBusiness = await db.businesses.get(businessId);
    if (!localHasBusiness) {
      if (!result.exists) {
        return ` The cloud confirms the business entity is missing. Re-publish it from the device that has it.`;
      }
      return ` The cloud reports the business exists but the snapshot lacks the entity row. Contact support.`;
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Ask the provider whether the cloud snapshot now carries a business row.
 *
 * Returns `null` when the answer is unavailable — an unsupported provider, a
 * network failure, or a malformed response. `null` is deliberately NOT folded
 * into `false`: "the cloud does not have it" and "we could not ask" lead to
 * different advice, and only one of them means the repair failed.
 *
 * This is the same capability `recoverBusinessEntity` uses to decide whether to
 * reset the outbox, re-read after the push because the question has changed: a
 * server that acknowledged the change is not the same claim as a snapshot that
 * returns the row. A prune can run in between.
 */
async function verifyCloudBusinessRow(
  provider: SyncProvider,
  businessId: string,
): Promise<boolean | null> {
  if (!provider.checkBusinessRowExists) return null;
  try {
    const result = await provider.checkBusinessRowExists({ businessId });
    if (!result || typeof result.exists !== 'boolean') return null;
    return result.exists;
  } catch {
    return null;
  }
}

/**
 * Cloud businesses NOT already present locally — the restore candidates.
 *
 * Ownership comes from the provider's getStatus (sync_list_businesses,
 * RLS-enforced server-side): account B never sees account A's ids, so there
 * is nothing to filter client-side. Anything already in `businesses` is
 * skipped, so the screen can never offer a restore that would duplicate a
 * business and a repeat restore is a no-op.
 */
export async function listRestorableCloudBusinesses(
  db: DNRDatabase,
  provider: SyncProvider = supabaseSyncProvider,
): Promise<CloudBusinessSummary[]> {
  const status = await provider.getStatus();
  if (status.state === 'unauthenticated') {
    throw new ProviderError(
      'NOT_AUTHENTICATED',
      status.message ?? 'Sign in to restore a synced business.',
      false,
    );
  }
  if (status.state === 'error' || status.state === 'disconnected') {
    throw new ProviderError(
      'UNREACHABLE',
      status.message ?? 'Could not reach the sync service.',
      true,
    );
  }
  const localIds = new Set((await db.businesses.toCollection().primaryKeys()).map(String));
  const candidates: CloudBusinessSummary[] = [];
  for (const businessId of status.authorisedBusinessIds) {
    if (localIds.has(businessId)) continue;
    let displayName: string | null = null;
    try {
      const snapshot = await provider.fetchSnapshot({ businessId });
      displayName = businessNameFrom(snapshot.changes, businessId);
    } catch {
      // Name is a courtesy, not a gate: an unreadable snapshot here must not
      // hide a restorable business. The restore itself surfaces the error.
    }
    candidates.push({ businessId, displayName });
  }
  return candidates;
}

/**
 * Restore a synced business onto a device that does not hold it.
 *
 * Same businessId throughout — nothing mints an id here. The business row
 * arrives through the snapshot like every other row (businessService enqueues
 * a 'business' change on create; sync_fetch_snapshot returns latest-per-
 * entity; applyChanges writes it via TABLE_FOR). No special-case row write,
 * no invented format.
 *
 * Order: guard exists → fetch snapshot → apply + cursor + active pointer in
 * ONE Dexie transaction → set provider → register device → engine.run() for
 * anything newer than the snapshot → return final cursor.
 *
 * Failure safety: a failure before the transaction commits leaves no partial
 * business; a failure after it leaves a complete local business that the next
 * Sync Now finishes catching up. A failed restore never corrupts startup.
 */
export async function restoreCloudBusiness(
  db: DNRDatabase,
  businessId: string,
  options?: {
    readonly deviceLabel?: string;
    readonly provider?: SyncProvider;
    readonly registerDevice?: DeviceRegistrar;
  },
): Promise<CloudRestoreResult> {
  if (!businessId) throw new ProviderError('REJECTED', 'A business id is required.', false);
  const provider = options?.provider ?? supabaseSyncProvider;
  const registerDevice = options?.registerDevice ?? registerSupabaseDevice;

  const already = await db.businesses.get(businessId);
  if (already) {
    await setSyncProvider(db, provider.id);
    await setActiveBusinessId(db, businessId);

    const deviceId = await currentDeviceId(db);
    const readCursor = async (): Promise<string> => {
      const state = await db.syncState
        .where('[businessId+deviceId+providerId]')
        .equals([businessId, deviceId, provider.id])
        .first();
      return state?.cursor ?? '';
    };

    // RECOVERY: the server may have lost the business entity — the buggy
    // sync_prune_compacted deleted latest-version rows whose cursor merely sat
    // below its pruning floor, and the business row is pushed first, so it is
    // always the lowest cursor in the log. `recoverBusinessEntity` asks the
    // server first and resets the single acked outbox entry to pending ONLY
    // when the server confirms the row is gone. Its validation is not
    // repeated, loosened or second-guessed here; this branch reports what it
    // decided and nothing more.
    const recovery = await recoverBusinessEntity(db, businessId, provider);

    // Recovery refused. Nothing was reset, so there is nothing to push — and
    // running the engine anyway would perform an ordinary sync under the
    // banner of a repair, which is indistinguishable from the upload the user
    // actually asked for.
    if ('code' in recovery) {
      return {
        businessId,
        applied: 0,
        skipped: 0,
        cursor: await readCursor(),
        recovered: false,
        pulled: 0,
        businessRepublished: false,
        repair: {
          changed: false,
          serverHadBusiness: false,
          error: { code: recovery.code, message: recovery.message },
          cloudHasBusiness: null,
          pushed: 0,
          failed: 0,
          applied: 0,
          conflicts: 0,
        },
      };
    }

    // Recovery either reset the entry (the entity was missing, so it needs the
    // push) or confirmed the server already holds it (so it needs the catch-up
    // this branch has always performed). Both permit the cycle.
    const engine = new SyncEngine({ db, provider });
    const run = await engine.run(businessId);

    // THE OUTCOME IS THE ENTRY'S, NOT THE RUN'S.
    //
    // `run()` never throws — it records the failure and returns — but its status
    // answers "did anything at all go wrong", not "did the business change
    // upload". It is `error` for a permanently failed entry belonging to an
    // unrelated invoice, and for a pull that failed after a successful push. The
    // previous reading classified both as a failed business upload AND skipped
    // the verification, so the one fact that settles the question was never
    // asked.
    //
    // The entry this repair reset is the precise answer:
    //   failed   → the provider refused it permanently; `lastError` says why
    //   acked    → the batch carrying it was accepted, whatever else failed
    //   pending  → deferred (a retryable failure); verify anyway, because the
    //              server may have accepted it before the response was lost
    const entry = await db.syncOutbox.get(recovery.changeId);
    // Only a publish THIS repair asked for can have been rejected by it. When the
    // server already held the row there was nothing to send, so a stale `failed`
    // entry from an earlier run is not news about this one.
    const uploadRejected = recovery.outboxReset && entry?.status === 'failed';
    const pushError = uploadRejected
      ? (entry?.lastError ?? `The sync server rejected the business change ${recovery.changeId}.`)
      : undefined;

    // Verify whenever the entry itself was not rejected — an unrelated pull
    // failure says nothing about whether the row arrived. Asking after a
    // rejected push would only report "missing" for a reason the push error
    // already explains better.
    const cloudHasBusiness =
      recovery.outboxReset && !uploadRejected
        ? await verifyCloudBusinessRow(provider, businessId)
        : null;

    return {
      businessId,
      applied: 0,
      skipped: 0,
      cursor: await readCursor(),
      recovered: run.recovered,
      pulled: run.pulled,
      businessRepublished: recovery.outboxReset,
      repair: {
        changed: recovery.outboxReset,
        serverHadBusiness: recovery.serverHadBusiness,
        cloudHasBusiness,
        pushError,
        entryStatus: entry?.status,
        pushed: run.pushed,
        failed: run.failed,
        applied: run.applied,
        conflicts: run.conflicts,
      },
    };
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new ProviderError(
      'UNREACHABLE',
      'No local business is available. Connect to the internet to restore an existing business.',
      true,
    );
  }

  let snapshot: {
    readonly changes: readonly SyncChange[];
    readonly cursor: { readonly token: string };
  };
  try {
    snapshot = await provider.fetchSnapshot({ businessId });
  } catch (error) {
    // Preserve the provider's own classification instead of flattening every
    // failure to FORBIDDEN. An unreachable/offline transport is retryable and
    // must say so; collapsing it to a non-retryable FORBIDDEN would tell a user
    // with a momentary connection drop that they are locked out for good.
    if (error instanceof ProviderError) throw error;
    throw new ProviderError(
      'FORBIDDEN',
      `Could not fetch the synced business: ${errorMessage(error)}`,
      false,
      {
        cause: error,
      },
    );
  }
  if (snapshot.changes.length === 0) {
    throw new ProviderError('REJECTED', 'The synced business has no data to restore.', false);
  }

  // PRE-FLIGHT: the change log must carry a business row for this id. Without it
  // the snapshot is a user's scratch log, and applying it would pin the app to a
  // business that never was. The business row is the first change the
  // businessService enqueues on create, so a real synced business always has it.
  const businessChange = snapshot.changes.find(
    (c) => c.entityType === 'business' && c.entityId === businessId,
  );
  if (!businessChange) {
    // The cloud snapshot lacks the business entity — likely the result of the
    // buggy sync_prune_compacted that deleted business rows below the pruning
    // floor. This device has no local copy to re-push from. The user must
    // recover from the device or backup that still has the business locally.
    const action = provider.checkBusinessRowExists
      ? await checkAndAdviseMissingBusiness(provider, businessId, db)
      : '';
    throw new ProviderError(
      'REJECTED',
      `The synced business snapshot has no business row for ${businessId}. Recover this business on the device that has it locally, then run Sync Now to republish it.` +
        action,
      false,
    );
  }

  const deviceId = await currentDeviceId(db);
  const now = new Date().toISOString();

  const applied = await db.transaction(
    'rw',
    [
      db.businesses,
      db.parties,
      db.categories,
      db.taxRates,
      db.products,
      db.stockLocations,
      db.documents,
      db.documentLines,
      db.documentLinks,
      db.payments,
      db.paymentAllocations,
      db.expenses,
      db.stockTransactions,
      db.syncState,
      db.syncConflicts,
      db.syncOutbox,
      db.settings,
    ],
    async () => {
      const result = await applyChanges({
        db,
        businessId,
        deviceId,
        changes: snapshot.changes,
        now,
        fromSnapshot: true,
      });
      await db.syncState.put({
        id: `${businessId}:${deviceId}:${provider.id}`,
        businessId,
        deviceId,
        providerId: provider.id,
        cursor: snapshot.cursor.token,
        lastSuccessfulSyncAt: now,
        state: 'idle',
      });
      await setActiveBusinessId(db, businessId);
      await healActiveBusinessPointer(db);
      return result;
    },
  );

  const restored = await db.businesses.get(businessId);
  if (!restored)
    throw new ProviderError('REJECTED', 'Restore applied but the business row is missing.', false);

  await setSyncProvider(db, provider.id);

  const localDevice = await ensureDevice(db, businessId, now);
  try {
    await registerDevice({
      businessId,
      deviceId,
      label: options?.deviceLabel ?? localDevice.label ?? 'This device',
      platform: localDevice.platform ?? 'unknown',
    });
  } catch (error) {
    throw new ProviderError(
      'UNREACHABLE',
      `Business restored locally, but this device could not be registered: ${errorMessage(error)}`,
      true,
      { cause: error },
    );
  }

  const engine = new SyncEngine({ db, provider });
  const run = await engine.run(businessId);
  const state = await db.syncState
    .where('[businessId+deviceId+providerId]')
    .equals([businessId, deviceId, provider.id])
    .first();

  return {
    businessId,
    applied: applied.applied,
    skipped: applied.skipped,
    cursor: state?.cursor ?? snapshot.cursor.token,
    recovered: run.recovered,
    pulled: run.pulled,
  };
}
